import { useQueries, useQuery } from '@tanstack/react-query';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import React, { useMemo, useEffect } from 'react';

import VersionsPage from '../../components/pages/v2/vehicle/versions';
import useBreadcrumbs from '../../components/pages/vehicle/hooks/use-breadcrumbs';
import { useAppContext } from '../../components/providers/app-context';
import {
  ADVANCED_VIEW_ACTIVE,
  DETAILED_PRICES,
  SHIPPING_LOCATION,
} from '../../static/cookies';
import getCategoryNames from '../../utils/get-category-names';
import { isGlobalUser } from '../../utils/is-global-user';
import { isUKuser } from '../../utils/is-uk-user';
import { isUSuser } from '../../utils/is-us-user';
import getGroups from '../../utils/react-query/query-options/get-groups';
import getLasEntries from '../../utils/react-query/query-options/get-last-entries';
import getLastVehicles from '../../utils/react-query/query-options/get-last-vehicles';
import getLoggedUser from '../../utils/react-query/query-options/get-logged-user';
import getStock from '../../utils/react-query/query-options/get-stocks';
import getTranslatedSlugs from '../../utils/react-query/query-options/get-translated-slugs';
import getVersions from '../../utils/react-query/query-options/get-versions';
import { redirectForAdvanced } from '../../utils/redirect-for-advanced';
import slugChange301 from '../../utils/slug-change-301';
import validateCategoryPartSlugs from '../../utils/validate-category-part-slugs';
import Cookies from 'js-cookie';
import forceShippingUKForCoUK from '../../utils/force-shipping-uk-for-co-uk';

const useData = () => {
  const {
    locale = 'en',
    query: { category = '', subCategory = '', model = '' },
  } = useRouter();
  const { detailedPrice } = useAppContext();

  const { selection } = useMemo(() => {
    const hasSelection = !!(category && subCategory);

    const selection = hasSelection
      ? {
        category: { category, subCategory },
      }
      : undefined;

    return { selection, hasSelection };
  }, [category, subCategory]);

  const queries = useMemo(
    () => ({
      queries: [
        getGroups({ locale }),
        getVersions({ locale, model }),
        getLasEntries({
          locale,
          part: subCategory as string || '',
          vehicle: model as string || '',
          detailed_prices: !!detailedPrice,
        }),
        getLastVehicles({
          locale,
          part: subCategory as string || '',
          vehicle: model as string || '',
        }),
      ].map(([key, fn]) => ({
        queryKey: key,
        queryFn: fn,
      })),
    }),
    [locale, model, subCategory]
  );

  const [
    { data: groups },
    { data: versions },
    { data: lastEntries, isFetched: isFetchedLastEntries },
    { data: vehicles },
  ] = useQueries(queries);

  // GTM
  useEffect(() => {
    if (isFetchedLastEntries && lastEntries.length > 0) {
      // @ts-ignore
      window.dataLayer.push({
        event: 'eec.impressionView',
        ecommerce: {
          currencyCode: 'EUR',
          impressions: lastEntries?.map(
            (item: TProductListItem, index: number) => ({
              list: 'Last Parts in Stock', // List name where the impression was viewed
              id: item.sku, // SKU of the product
              name: item.part_en, // name of the product.
              brand: item.car_brand, // car brand of the part
              category: item.category_en_old, // Product category(ies) - each "/" is a category level (max 5 levels)
              price: item.pricing?.price_product
                ? Number(item.pricing.price_product).toFixed(2)
                : null, // price of the part without shipping, tax and currency
              position: index, // the position of the item in the list
              dimension2: item.reference, // reference of the product
              dimension3: item.car_model, // car model of the part
              dimension4: item.car_version, // car version of the part
            })
          ),
        },
      });

      // GA4
      window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
      window.dataLayer.push({
        event: 'view_item_list',
        ecommerce: {
          currency: 'EUR', // Currency of the items associated with the event, in 3-letter ISO 4217 format
          items: lastEntries?.map((item: TProductListItem, index: number) => ({
            item_list_name: 'Last Parts in Stock', // List name where the impression was viewed
            item_list_id: 'last-parts-in-stock', // List name where the impression was viewed
            item_id: item.sku, // SKU of the product
            item_name: item.part_en, // name of the product.
            item_brand: item.car_brand, // car brand of the part
            item_category: item.category_en, // Product category(ies) - each "/" is a category level (max 5 levels)
            item_category2: item.part_en, // part name === product name
            price: item.pricing?.price_product
              ? Number(item.pricing.price_product).toFixed(2)
              : null, // price of the part without shipping, tax and currency
            index: index, // the position of the item in the list
            quantity: '1', // Hardcoded to 1
          })),
        },
      });
    }
  }, [lastEntries, isFetchedLastEntries]);

  const vehicle = useMemo(() => {
    return versions?.info;
  }, [versions]);

  const { data: stock } = useQuery(
    ...getStock({
      locale,
      stocks: [
        {
          part: subCategory,
          vehicle: model,
        },
        {
          vehicle: versions?.info.brand.slug,
        },
      ],
    })
  );

  const { data: translatedSlugs } = useQuery(
    ...getTranslatedSlugs({
      lang_origin: locale,
      lang_trans: null,
      slugs: [
        {
          slug_origin: category as string,
          slug_type: 'category',
        },
        {
          slug_origin: subCategory as string,
          slug_type: 'part',
        },
      ],
    }),
    {
      enabled: !!subCategory && !!category,
      keepPreviousData: true,
    }
  );

  const breadcrumbs = useBreadcrumbs({
    selection,
    groups,
    brand: vehicle.brand,
    model: vehicle.model,
  });
  

  const info = useMemo(
    () =>
      selection?.category ? getCategoryNames(selection.category, groups) : null,
    [selection, groups]
  );

  return {
    groups,
    selection,
    vehicle,
    info,
    breadcrumbs,
    stock,
    vehicles,
    lastEntries,
    versions,
    translatedSlugs,
  };
};

export default function Versions() {
  const data = useData();
  return (
    <main>
      <VersionsPage {...data} />
    </main>
  );
}

export const getServerSideProps: GetServerSideProps = async ({
  locale = 'en',
  req,
  res,
  query,
  params,
}) => {
  isUSuser(req, res);
  isUKuser(req, res);
  isGlobalUser(req, res);
  await forceShippingUKForCoUK({ req, res, locale });

  const { category = '', subCategory = '', model = '' } = query;
  const { default: getGenericClient } = await import('../../server/client');
  const genericClient = getGenericClient();
  const { default: retrieveStateAndClear } = await import(
    '../../utils/retrieve-state-and-clear'
  );

  try {
    const [user, groups, versions] = await Promise.all([
      genericClient.fetchQuery(...getLoggedUser({ locale }, req)),
      genericClient.fetchQuery(...getGroups({ locale, vehicle: model }, req)),
      genericClient.fetchQuery(...getVersions({ locale, model }, req)),
      category &&
      subCategory &&
      genericClient.prefetchQuery(
        ...getTranslatedSlugs(
          {
            lang_origin: locale,
            lang_trans: null,
            slugs: [
              {
                slug_origin: category as string,
                slug_type: 'category',
              },
              {
                slug_origin: subCategory as string,
                slug_type: 'part',
              },
            ],
          },
          req
        )
      ),
      genericClient.prefetchQuery(
        ...getLasEntries(
          {
            locale,
            part: subCategory as string || '',
            vehicle: model as string || '',
            detailed_prices: req.cookies[DETAILED_PRICES] === 'true',
          },
          req
        )
      ),
      genericClient.prefetchQuery(
        ...getLastVehicles(
          {
            locale,
            part: subCategory as string || '',
            vehicle: model as string || '',
          },
          req
        )
      ),
    ]);

    await genericClient.prefetchQuery(
      ...getStock(
        {
          locale,
          stocks: [
            {
              part: subCategory as string || '',
              vehicle: model as string || '',
            },
            {
              vehicle: versions?.info?.brand.slug,
            },
          ],
        },
        req
      )
    );

    await validateCategoryPartSlugs({
      locale,
      groups,
      category: category as string,
      subCategory: subCategory as string,
      req,
    });

    if (
      user?.staff?.is_pro &&
      !!parseInt(req.cookies[ADVANCED_VIEW_ACTIVE] || '0')
    ) {
      return redirectForAdvanced({
        locale,
        vehicle: model as string,
        category: category as string,
        subCategory: subCategory as string,
      });
    }

    return {
      props: {
        dehydratedState: retrieveStateAndClear(genericClient),
        shippingLocation: parseInt(req.cookies[SHIPPING_LOCATION], 10),
        detailPrices: req.cookies[DETAILED_PRICES] || null,
      },
    };
  } catch (e) {
    if (e.status === 301) {
      const newQuery: { [key: string]: string } = {
        ...params,
        ...(e.data.category && { category: e.data.category }),
        ...(e.data.part && { subCategory: e.data.part }),
        ...(e.data.model && { model: e.data.model }),
      };

      return slugChange301({
        locale,
        query: newQuery,
        res,
        page: params?.category ? 'versions-with-part' : 'versions',
      });
    } else if (e.status === 404) {
      if (
        e?.data?.detail === 'part_identifier' ||
        e?.data?.detail === 'group_identifier'
      ) {
        return slugChange301({
          locale,
          query: {
            model: params?.model as string,
          },
          res,
          page: 'versions',
          status: 307,
        });
      }

      return {
        notFound: true,
      };
    }

    throw e;
  }
};
